/**
 * Job Queue Types - Discriminated Union Approach
 *
 * This file defines type-safe job structures using discriminated unions.
 * Each job type (render/album/upscale) has its own specific fields.
 * TypeScript can now infer the correct type based on jobType.
 */

import type { RenderStatus } from '@/shared/constants';

// ===== Common Job Fields =====
interface BaseJob {
  id: string;
  status: (typeof RenderStatus)[keyof typeof RenderStatus];
  startedAt: string | null;
  durationPredicted: number;
  attemptNumber: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  // NO progress field - calculated locally in components using calculateTimeBasedProgress()

  // Project/Concept metadata
  conceptId: string;
  projectId: string;
  conceptName: string | null;
  conceptSequenceNumber: number;
  projectName: string;
}

// ===== Render Job =====
/**
 * Render job - represents NEW facade design generation
 *
 * ARCHITECTURE NOTE:
 * - Creates NEW render record in database
 * - Main angle renders create new concepts
 * - Derivative renders add to existing render stack
 * - Frontend treats render as ADDITION to render list
 * - Auto-scroll should trigger on render completion
 */
export interface RenderJob extends BaseJob {
  jobType: 'render';
  scenarioType?: string; // Backend scenario type

  // Required for renders
  conceptAngleId: string;
  angleType: 'main' | 'derivative';

  // Render-specific
  imageUrl: string | null; // Render image URL (if completed)
  angleUrl: string | null; // Original angle image URL
  isUpscaled?: boolean; // True if this render has been upscaled to 4K
}

// ===== Album Job =====
export interface AlbumJob extends BaseJob {
  jobType: 'album';

  // Albums are concept-level (no specific angle)
  conceptAngleId: null;
  angleType: null;

  // Album-specific
  albumTemplate: string;
  // Whether blueprints were bundled into this album (server-driven). Replaces
  // the old `template = full/short` distinction for display purposes.
  albumIncludeBlueprints: boolean;
  albumFormat: string;
  albumLanguage: string;
  albumFilename: string | null;
  albumPublicUrl: string | null; // Direct S3 URL for completed albums
}

// ===== Estimate Job =====
/**
 * Estimate job - cost-estimate generation for a concept.
 *
 * Rides the same job pipeline as albums (queue snapshot, history feed,
 * lifecycle WS events on the `estimates` entity). Estimates never consume
 * render slots — the server aggregate counts only renders.
 */
export interface EstimateJob extends BaseJob {
  jobType: 'estimate';

  // Estimates are concept-level (no specific angle)
  conceptAngleId: null;
  angleType: null;

  // Estimate-specific (display currency; stored amounts are USD server-side)
  estimateCurrency: string | null;
  estimateGrandTotal: number | null;
  estimateRegionLabel: string | null;
}

// ===== Upscale Job =====
/**
 * Upscale job - represents 4K upscaling of existing renders
 *
 * ARCHITECTURE NOTE:
 * - Backend creates a separate Render record with scenario_type='upscale' (soft-deleted)
 * - On completion, updates ORIGINAL render with is_upscaled=true and upscaled file_url
 * - Frontend treats upscale as an UPDATE to existing render, NOT a new render
 * - Auto-scroll and totalRenders logic should EXCLUDE upscale jobs
 */
export interface UpscaleJob extends BaseJob {
  jobType: 'upscale';
  scenarioType: 'upscale';

  // Upscale is angle-specific
  conceptAngleId: string;
  angleType: 'main' | 'derivative';

  // Upscale-specific
  originalRenderId: string; // ID of the original render being upscaled
  imageUrl: string | null; // Upscaled image URL (if completed)
  angleUrl: string | null; // Original angle image URL
}

// ===== Discriminated Union =====
/**
 * Job type - discriminated union for type safety
 * TypeScript can now narrow the type based on jobType field
 *
 * Example:
 * ```typescript
 * function handleJob(job: Job) {
 *   if (job.jobType === 'album') {
 *     // TypeScript knows job.albumPublicUrl exists here
 *     console.log(job.albumPublicUrl);
 *   }
 * }
 * ```
 */
export type Job = RenderJob | AlbumJob | UpscaleJob | EstimateJob;

// ===== Control Generation Job =====
// Controls are separate from the main queue (different lifecycle)
export interface ControlJob {
  id: string;
  status: (typeof RenderStatus)[keyof typeof RenderStatus];
  controlType: 'lineart' | 'depth' | 'normal' | 'draft';

  // Controls belong to angles
  angleId: string;
  conceptId?: string; // Optional for tracking
  projectId?: string; // Optional for tracking

  startedAt: string | null;
  createdAt: string;
  completedAt: string | null;
  // NO progress field - calculated locally in components (fixed 45s duration)
  errorMessage?: string;
}

// ===== Type Guards =====
/**
 * Type guard to check if a job is a RenderJob
 */
export function isRenderJob(job: Job): job is RenderJob {
  return job.jobType === 'render';
}

/**
 * Type guard to check if a job is an AlbumJob
 */
export function isAlbumJob(job: Job): job is AlbumJob {
  return job.jobType === 'album';
}

/**
 * Type guard to check if a job is an UpscaleJob
 */
export function isUpscaleJob(job: Job): job is UpscaleJob {
  return job.jobType === 'upscale';
}

/**
 * Type guard to check if a job is an EstimateJob
 */
export function isEstimateJob(job: Job): job is EstimateJob {
  return job.jobType === 'estimate';
}
